/**
 * msalConfig.js
 *
 * Microsoft Authentication Library (MSAL) configuration for SharePoint integration.
 * Uses SPA PKCE flow — tokens acquired in browser, passed to backend as Bearer headers.
 *
 * When VITE_AZURE_CLIENT_ID is not set, all exports are no-ops / null.
 */

import { PublicClientApplication, InteractionRequiredAuthError, CacheLookupPolicy } from '@azure/msal-browser';

const clientId = import.meta.env.VITE_AZURE_CLIENT_ID || '';
// The Microsoft sign-in here is ONLY for SharePoint, and the SharePoint
// belongs to the CUSTOMER's tenant, never pidly's. The authority is
// therefore `organizations` (a work account in any tenant); a tenant id
// pinned in VITE_AZURE_TENANT_ID used to be the authority, and a user from
// any other tenant was refused at Microsoft's door ("does not exist in
// tenant pidly"). The variable is still honoured for a deliberate
// single-tenant deployment, but only when VITE_AZURE_SINGLE_TENANT is set.
const pinnedTenant = import.meta.env.VITE_AZURE_TENANT_ID || '';
const singleTenant = String(import.meta.env.VITE_AZURE_SINGLE_TENANT || '') === '1';
const authorityTenant = singleTenant && pinnedTenant ? pinnedTenant : 'organizations';

export const isMsalConfigured = !!clientId;

const msalConfig = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${authorityTenant}`,
    redirectUri: window.location.origin + '/',
    postLogoutRedirectUri: window.location.origin + '/',
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
};

// Default scopes for a regular pidly user. Sites.FullControl.All used
// to live here too but it's only needed for the workspace-admin
// Sites.Selected setup; folding it in surfaced an extra consent step
// for everyone, which made the SharePoint sign-in feel heavier than
// it needs to be. Admin code paths now request the elevated scope
// explicitly via `adminGraphScopes`.
export const loginRequest = {
  scopes: ['User.Read', 'Files.ReadWrite.All', 'Sites.ReadWrite.All'],
};

const graphScopes = {
  scopes: ['Files.ReadWrite.All', 'Sites.ReadWrite.All'],
};

// Used only by SharePointAppConnect when an admin actually needs to
// call POST /sites/{id}/permissions. Requesting this scope triggers
// a consent screen the first time, which is the appropriate gate —
// it's the admin agreeing to give pidly elevated rights.
const adminGraphScopes = {
  scopes: ['Sites.FullControl.All', 'Sites.ReadWrite.All'],
};

let msalInstance = null;

export function getMsalInstance() {
  if (!msalInstance && isMsalConfigured) {
    msalInstance = new PublicClientApplication(msalConfig);
  }
  return msalInstance;
}

/**
 * Returns an access token for Microsoft Graph API.
 * Tries silent acquisition; if that fails (e.g. needs consent/interaction),
 * falls back to a full-page redirect login. Popup flow is intentionally
 * avoided — it was unreliable behind proxies / certain browsers.
 *
 * Pass `{ silent: true }` to throw instead of triggering a login redirect.
 * Use this for background code paths (auto-recovery, polling, etc.) where
 * a sudden full-page redirect to Microsoft would dump the user's session.
 */
// ── Silent token requests never open a hidden iframe ──────────────────
// MSAL's silent flow falls back to a hidden iframe against
// login.microsoftonline.com when the cache holds no usable refresh token.
// Asked from the SharePoint lock refresh every minute, that left one new
// iframe (a whole Microsoft sign-in page) in the DOM per attempt and never
// removed it: an idle workspace grew past 1 GB and Chrome killed the tab.
// Silent now means the cache or a refresh token only, a failure is
// remembered for ten minutes so the pollers do not ask again, and any frame
// MSAL did leave behind is removed.
const SILENT_RETRY_MS = 10 * 60 * 1000;
let silentBlockedUntil = 0;
function removeMsalFrames() {
  try {
    document.querySelectorAll('iframe[src*="login.microsoftonline.com"], iframe[src*="oauth2/v2.0/authorize"]').forEach(f => f.remove());
  } catch { /* no DOM */ }
}
async function silentToken(instance, request) {
  if (Date.now() < silentBlockedUntil) {
    const err = new Error('Microsoft sign-in is not available silently right now.');
    err.code = 'MSAL_SILENT_BLOCKED';
    throw err;
  }
  try {
    return await instance.acquireTokenSilent({ ...request, cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken });
  } catch (e) {
    silentBlockedUntil = Date.now() + SILENT_RETRY_MS;
    removeMsalFrames();
    throw e;
  }
}

export async function acquireGraphToken({ silent = false } = {}) {
  const instance = getMsalInstance();
  if (!instance) throw new Error('MSAL not configured — set VITE_AZURE_CLIENT_ID');

  await instance.initialize().catch(() => {}); // ensure MSAL is ready

  let accounts = instance.getAllAccounts();

  // No signed-in account.
  if (accounts.length === 0) {
    if (silent) {
      const err = new Error('SharePoint sign-in required.');
      err.code = 'MSAL_NO_ACCOUNT';
      throw err;
    }
    // Trigger interactive login via redirect.
    // NOTE: do NOT set reopenSharePointBrowser here. acquireGraphToken is called
    // by many code paths (project load, file refresh, checkout/checkin, etc.) and
    // setting the flag unconditionally would pop the SharePoint browser open
    // after every sign-in even when the user never asked for it. The browser's
    // own sign-in button sets that flag itself.
    await instance.loginRedirect(loginRequest);
    throw new Error('Redirecting to Microsoft sign-in…');
  }

  try {
    const response = await silentToken(instance, { ...graphScopes, account: accounts[0] });
    return response.accessToken;
  } catch (error) {
    if (silent) {
      const err = new Error('SharePoint token expired.');
      err.code = 'MSAL_INTERACTION_REQUIRED';
      throw err;
    }
    // Silent acquisition failed — need interactive consent. Use redirect.
    await instance.acquireTokenRedirect(graphScopes);
    throw new Error('Redirecting to Microsoft for consent…');
  }
}

/**
 * Try to get a Graph token without user interaction. Returns the token
 * or null if no MSAL account is signed in / silent acquisition failed.
 * Used by SharePoint data-plane calls that can fall back to pidly's
 * app-only token on the backend when the user isn't signed in to
 * Microsoft — so we don't want to trigger a redirect in that case.
 */
export async function maybeAcquireGraphToken() {
  if (!isMsalConfigured) return null;
  try {
    return await acquireGraphToken({ silent: true });
  } catch {
    return null;
  }
}

/**
 * Try to get a Graph token with admin-level scopes (Sites.FullControl.All)
 * silently. Returns null if not available — the caller surfaces a
 * "Sign in to continue" prompt that triggers triggerAdminMicrosoftSignIn
 * on user click. Used exclusively by the Sites.Selected admin flow.
 */
export async function maybeAcquireAdminGraphToken() {
  if (!isMsalConfigured) return null;
  const instance = getMsalInstance();
  if (!instance) return null;
  try {
    await instance.initialize().catch(() => {});
  } catch { return null; }
  const accounts = instance.getAllAccounts();
  if (accounts.length === 0) return null;
  try {
    const response = await silentToken(instance, { ...adminGraphScopes, account: accounts[0] });
    return response.accessToken;
  } catch {
    return null;
  }
}

/**
 * Trigger an interactive consent for the admin scopes (Sites.FullControl.All).
 * Use this when maybeAcquireAdminGraphToken returns null AND the user
 * has explicitly clicked a "Sign in to continue" affordance — don't
 * call it unprompted, the redirect throws the user out of whatever
 * dialog they were filling in.
 */
export async function triggerAdminMicrosoftSignIn() {
  const instance = getMsalInstance();
  if (!instance) throw new Error('MSAL not configured');
  await instance.initialize().catch(() => {});
  const accounts = instance.getAllAccounts();
  if (accounts.length === 0) {
    await instance.loginRedirect({ scopes: [...loginRequest.scopes, ...adminGraphScopes.scopes] });
  } else {
    await instance.acquireTokenRedirect(adminGraphScopes);
  }
}

/**
 * Trigger an interactive Microsoft sign-in (full-page redirect).
 * Use this when the user has confirmed they want to sign in — e.g. from
 * a popup after a silent token acquisition failed.
 */
export async function triggerMicrosoftSignIn() {
  const instance = getMsalInstance();
  if (!instance) throw new Error('MSAL not configured');
  await instance.initialize().catch(() => {});
  const accounts = instance.getAllAccounts();
  if (accounts.length === 0) {
    await instance.loginRedirect(loginRequest);
  } else {
    await instance.acquireTokenRedirect(graphScopes);
  }
}

/**
 * Returns an ID token for the backend API (audience = client ID).
 * Returns null if MSAL is not configured or user is not signed in.
 */
export async function acquireBackendToken() {
  const instance = getMsalInstance();
  if (!instance) return null;
  const accounts = instance.getAllAccounts();
  if (accounts.length === 0) return null;
  try {
    const response = await silentToken(instance, { scopes: ['openid', 'profile', 'email'], account: accounts[0] });
    return response.idToken;
  } catch {
    return null;
  }
}

/**
 * Returns the signed-in user's display name, or null.
 */
export function getMsalUserName() {
  const instance = getMsalInstance();
  if (!instance) return null;
  const accounts = instance.getAllAccounts();
  return accounts.length > 0 ? accounts[0].name || accounts[0].username : null;
}

/**
 * True if there is at least one signed-in MSAL account. Use this to gate
 * background SharePoint work so we don't trigger an unwanted redirect for
 * users who haven't signed in to Microsoft yet.
 */
export function hasMsalAccount() {
  if (!isMsalConfigured) return false;
  try {
    const instance = getMsalInstance();
    if (!instance) return false;
    return instance.getAllAccounts().length > 0;
  } catch {
    return false;
  }
}

/**
 * Verify SharePoint access without triggering an interactive sign-in.
 * Returns 'ok' if a Graph token can be acquired silently, 'no_account'
 * if there's no MSAL session, or 'expired' if a token request failed
 * (e.g. consent revoked, refresh token expired).
 *
 * Use this at project-open time to check the user's MSAL state upfront
 * and prompt them ONCE before any SharePoint work runs in the background.
 */
export async function checkSharePointAccess() {
  if (!isMsalConfigured) return 'ok'; // SP not configured, nothing to check
  const instance = getMsalInstance();
  if (!instance) return 'no_account';
  try {
    await instance.initialize().catch(() => {});
  } catch {
    return 'no_account';
  }
  const accounts = instance.getAllAccounts();
  if (accounts.length === 0) return 'no_account';
  try {
    await silentToken(instance, { ...graphScopes, account: accounts[0] });
    return 'ok';
  } catch {
    return 'expired';
  }
}
